import { motion } from 'framer-motion';
import { 
  ClipboardCheck, 
  Search, 
  Settings, 
  CheckCircle2, 
  FileOutput
} from 'lucide-react';

const steps = [
  {
    icon: ClipboardCheck,
    number: '01',
    title: 'Vehicle Intake',
    description: 'Vehicle identification, damage assessment, and system verification.',
  },
  {
    icon: Search,
    number: '02',
    title: 'OEM Data Lookup',
    description: 'Retrieve manufacturer calibration specifications and requirements.',
  },
  {
    icon: Settings,
    number: '03',
    title: 'Calibration Setup',
    description: 'Position targets, verify environment, and configure equipment.',
  },
  {
    icon: CheckCircle2,
    number: '04',
    title: 'Validation',
    description: 'Post-calibration testing and system verification scans.',
  },
  {
    icon: FileOutput,
    number: '05',
    title: 'Report Issuance',
    description: 'Complete documentation with all calibration data and sign-off.',
  },
];

export const Process = () => {
  return (
    <section
      id="process"
      data-testid="process-section"
      className="py-20 md:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-blue-600 text-sm font-semibold tracking-widest uppercase mb-4 block">
            Our Process
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4">
            How We Work
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-base sm:text-lg">
            A systematic approach to ADAS calibration that ensures accuracy, 
            compliance, and complete traceability.
          </p>
        </motion.div>

        {/* Process Steps - Desktop */}
        <div className="hidden lg:block relative">
          {/* Connecting Line */}
          <div className="absolute top-20 left-[10%] right-[10%] h-0.5 bg-slate-200">
            <div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-blue-600 to-blue-400" />
          </div>

          <div className="grid grid-cols-5 gap-4 relative">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center"
                  data-testid={`process-step-${index}`}
                >
                  {/* Icon Circle */}
                  <div className="relative mx-auto w-16 h-16 mb-6">
                    <div className="absolute inset-0 bg-white rounded-full border-4 border-slate-200 flex items-center justify-center z-10">
                      <Icon className="w-7 h-7 text-blue-600" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center z-20">
                      <span className="text-white text-xs font-bold">{step.number}</span>
                    </div>
                  </div>

                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Process Steps - Mobile */}
        <div className="lg:hidden space-y-6">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex gap-4 items-start"
                data-testid={`process-step-mobile-${index}`}
              >
                <div className="flex-shrink-0 relative">
                  <div className="w-14 h-14 bg-white rounded-full border-2 border-blue-600 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{step.number}</span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="absolute top-14 left-1/2 -translate-x-1/2 w-0.5 h-8 bg-slate-200" />
                  )}
                </div>
                <div className="pt-2">
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-1">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
